import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import clsx from 'clsx'

import dayjs from 'dayjs'

import {
  useCompleteDistribution,
  useDistributionDailySeriesApi,
  useDistributionGetApi,
  useDistributionStatsApi
} from '@/common/api/modules/recommendationDistribution'
import {
  DistributionDailySeriesResponse,
  DistributionStatsResponse,
  DistributionVariantStat
} from '@/common/api/params/recommendationDistribution'
import { currencyText } from '@/common/app/currency'
import {
  abCampaignGoalMetricName,
  abCampaignTargetDeviceName,
  abCampaignTargetSegmentsName
} from '@/common/app/modules/recommend/abCampaign'
import {
  distributionStatusBadgeType,
  distributionStatusName
} from '@/common/app/modules/recommend/recommendationDistribution'
import { distributionEditPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateStr, dateTimeStr } from '@/common/form/date'
import {
  DistributionVariant,
  RecommendationDistribution,
  RecommendationDistributionStatus
} from '@/common/model/recommend/recommendationDistribution'
import { commaSeparatedNumber, formatPercent } from '@/common/util/number'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import MultiLineChart, { MULTI_LINE_COLORS } from '@/components/view/atoms/chart/MultiLineChart'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconInversion, IconType } from '@/components/view/atoms/icon/Icon'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import GuidanceAreaContent from '@/components/view/layouts/area/GuidanceAreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import MessageBar from '@/components/view/molecules/bar/MessageBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import MetricItem from '@/components/view/molecules/metric/MetricItem'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  ConversionAttribution,
  DEFAULT_CONVERSION_ATTRIBUTION,
  pickConversion
} from '@/common/model/recommend/conversion'
import ConversionAttributionToggle from '@/components/container/modules/recommend/ConversionAttributionToggle'
import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

const variantColor = (index: number) => MULTI_LINE_COLORS[index % MULTI_LINE_COLORS.length]

/** 判定に使ったコンバージョン種別の表示名 */
const ATTRIBUTION_LABEL: Record<ConversionAttribution, string> = {
  [ConversionAttribution.DIRECT]: '直接',
  [ConversionAttribution.ASSIST]: 'アシスト'
}

/** KPIカード（パターンごとの主要指標） */
const VariantKpiCard = ({
  variant,
  distributionVariant,
  index,
  target,
  isSuperior,
  isWinner,
  disabled,
  attribution,
  onComplete
}: {
  variant: DistributionVariantStat
  // 配信から外れたパターンの実績には対応する配信のパターンが無い
  distributionVariant?: DistributionVariant
  index: number
  target: number
  isSuperior: boolean
  isWinner: boolean
  disabled: boolean
  attribution: ConversionAttribution
  onComplete: () => void
}) => {
  return (
    <Area tableWrap borderColor={isWinner ? 'emphasis' : isSuperior ? 'success' : undefined}>
      <Heading
        rightNode={
          <Badge
            size={10}
            badgeType={
              isWinner
                ? BadgeType.EMPHASIS_SECONDARY
                : isSuperior
                  ? BadgeType.SUCCESS
                  : BadgeType.SECONDARY
            }
          >
            {isWinner ? '確定済み' : isSuperior ? '優位' : 'コントロール'}
          </Badge>
        }
        noTopBorder={isWinner || isSuperior}
      >
        <span
          style={{ color: disabled ? '' : variantColor(index) }}
          className={disabled ? 'text-disabled' : ''}
        >
          パターン {variant.group}
        </span>
      </Heading>
      <AreaContent disabled={disabled}>
        <FormRow gap={4} align='end'>
          <Text size={12}>トラフィック配分</Text>
          <ItemAlign align='end' gapX={2}>
            <Text
              size={16}
              color={distributionVariant?.ab_traffic == 100 ? TextColor.EMPHASIS : undefined}
              weight={distributionVariant?.ab_traffic == 100 ? 'bold' : undefined}
            >
              {distributionVariant?.ab_traffic ?? '-'}
            </Text>
            <Text size={12}>%</Text>
          </ItemAlign>
        </FormRow>
        <FormRow gap={16} align='end'>
          <Text size={12} weight='bold'>
            クリック率
          </Text>
          <ItemAlign align='end' gapX={4}>
            <span
              className='text-size-24 leading-100 font-bold'
              style={{ color: variantColor(index) }}
            >
              {variant.clicks / variant.impressions
                ? ((variant.clicks / variant.impressions) * 100).toFixed(1)
                : '0'}
            </span>
            <Text size={12}>%</Text>
          </ItemAlign>
        </FormRow>
        <div className='flex flex-wrap gap-4'>
          <ItemAlign gapX={4}>
            <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={14}>
              表示数
            </Badge>
            <Text>{commaSeparatedNumber(variant.impressions)}</Text>
          </ItemAlign>
          <ItemAlign gapX={4}>
            <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={14}>
              クリック数
            </Badge>
            <Text>{commaSeparatedNumber(variant.clicks)}</Text>
          </ItemAlign>
          <ItemAlign gapX={4}>
            <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={14}>
              CV数
            </Badge>
            <Text>{commaSeparatedNumber(pickConversion(variant, attribution).conversions)}</Text>
          </ItemAlign>
          <ItemAlign gapX={4}>
            <Badge badgeType={BadgeType.SQUARE_CRITICAL} size={14}>
              売上高
            </Badge>
            <Text weight='bold' size={20}>
              {currencyText(Math.round(pickConversion(variant, attribution).revenue))}
            </Text>
          </ItemAlign>
        </div>
        <InsideArea color={BgColor.SECONDARY} border>
          <div className='mb-1'>
            <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
              サンプル充足度
            </Badge>
          </div>
          <ItemAlign align='center' gapX={4}>
            <Text
              size={16}
              color={variant.impressions >= target ? TextColor.SUCCESS : TextColor.PRIMARY}
              weight='bold'
            >
              {commaSeparatedNumber(variant.impressions)}
            </Text>
            <Text size={16}>/{commaSeparatedNumber(target)}件</Text>
            {variant.impressions >= target && (
              <Badge badgeType={BadgeType.SUCCESS} size={12}>
                達成
              </Badge>
            )}
          </ItemAlign>
        </InsideArea>
        <ItemAlign justify={'center'}>
          <Button size='large' onClick={() => onComplete()} disabled={disabled || isWinner}>
            このパターンで確定する
          </Button>
        </ItemAlign>
      </AreaContent>
    </Area>
  )
}

/** 累積指標推移チャート（daily-series の cumulative_rate を描画） */
const TrendChart = ({
  metric,
  series
}: {
  metric: DistributionStatsResponse['goal_metric']
  series: DistributionDailySeriesResponse['series']
}) => {
  const labels = series.map((s) => dayjs(s.date).format('M/DD'))
  const groups = [...new Set(series.flatMap((s) => Object.keys(s.variants)))].sort()
  const chartItems = groups.map((group) => ({
    title: group,
    values: series.map((s) => {
      const v = s.variants[group]
      return v ? +(v.cumulative_rate * 100).toFixed(2) : null
    })
  }))

  if (chartItems.length === 0 || labels.length === 0) {
    return null
  }

  return (
    <Area noBorder>
      <ChartWrapper>
        <ChartTitle>累積{abCampaignGoalMetricName(metric)}推移</ChartTitle>
        <MultiLineChart labels={labels} items={chartItems} unit='%' />
      </ChartWrapper>
    </Area>
  )
}

/** 実績の分析表示 */
const AbResultView = ({
  siteId,
  distribution,
  onMutated
}: {
  siteId: number
  distribution: RecommendationDistribution
  onMutated: () => void
}) => {
  const vars = { site_id: siteId, distribution_id: distribution.id }
  const { data: stats, isLoading: statsLoading, mutate } = useDistributionStatsApi(vars)
  const { data: dailySeries, isLoading: seriesLoading } = useDistributionDailySeriesApi(vars)
  const { showModal, hide } = useContext(ConfirmDialogContext)

  const { addMessage } = useContext(ToastMessageContext)
  const { trigger: complete } = useCompleteDistribution({
    onSuccess: () => {
      mutate()
      onMutated()
      hide()
      addMessage({
        type: 'success',
        text: `パターンを確定しました。`
      })
    }
  })

  const [isSettingOpening, setIsSettingOpening] = useState(false)
  // 表示するコンバージョン種別。**判定はサーバー側で配信の設定どおりに確定済み**なので、
  // このトグルは表示だけを切り替える（画面から判定を動かせると、同じ配信の勝者が見る人によって変わる）
  const [attribution, setAttribution] = useState<ConversionAttribution>(
    DEFAULT_CONVERSION_ATTRIBUTION
  )

  if (statsLoading || seriesLoading) {
    return <PageListLoading />
  }
  if (!stats) {
    return null
  }

  const metric = stats.goal_metric
  const significance = stats.significance
  const winner = significance?.winner ?? null
  // 指標サマリーは目標指標（rate）の高い順に並べ、表示順位を優位順とする
  const rankedVariants = [...stats.variants].sort((a, b) => b.rate - a.rate)
  const confidencePercent = (
    (significance?.confidence_level ?? distribution.confidence_level) * 100
  ).toFixed(0)

  return (
    <Content>
      {!distribution.ab_campaign?.winner_variant && significance?.is_significant && winner && (
        <MessageBar type='success' fillIn>
          <Text size={14} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
            パターン {winner} が統計的に優位です（信頼度 {confidencePercent}%）
          </Text>
          <br />
          <Text size={12}>
            p値 {significance.p_value.toFixed(3)} ・ +{significance.lift_percent.toFixed(1)}% lift
            ・ パターン {winner} への確定を検討してください
          </Text>
        </MessageBar>
      )}

      {/* A/Bテスト設定サマリー */}
      <div className='border-tertiary rounded-xl border p-4'>
        <div className='flex w-full items-center justify-between'>
          <div className='mb-1 flex items-center gap-2'>
            <Text size={14} weight='bold'>
              A/Bテスト設定
            </Text>
            <TextButton link={distributionEditPath(siteId, distribution.id)} arrow>
              配信状況を確認する
            </TextButton>
          </div>
          <Button
            color={ButtonColor.SECONDARY}
            square
            onClick={() => setIsSettingOpening((isOpening) => !isOpening)}
          >
            <Icon
              type={IconType.ARROW_DOWN}
              inversion={isSettingOpening ? IconInversion.VERTICAL : undefined}
            />
          </Button>
        </div>
        <div className={clsx('accordion', isSettingOpening ? 'opening' : '')}>
          <ItemAlign align='center' gapX={8}>
            <MetricItem label='KPI目標' value={abCampaignGoalMetricName(metric)} />
            <MetricItem
              label='信頼度'
              value={`${(distribution.confidence_level * 100).toFixed(0)}%`}
            />
            <MetricItem
              label='デバイス'
              value={distribution.target_devices
                .map((d) => abCampaignTargetDeviceName(d))
                .join(' / ')}
            />
            <MetricItem
              label='対象セグメント'
              value={distribution.target_segments
                .map((s) => abCampaignTargetSegmentsName(s))
                .join(', ')}
            />
          </ItemAlign>
        </div>
      </div>

      {/* 判定指標。何で比較して勝者を出したのかを、結果の直上に常時表示する */}
      <div className='bg-fill-surface-tertiary rounded-md px-4 py-3'>
        <ItemAlign align='center' gapX={16}>
          <Text size={13} weight='bold'>
            判定指標
          </Text>
          <Text size={13}>
            {stats.goal_metric === 'ctr'
              ? 'クリック率'
              : `コンバージョン率（${ATTRIBUTION_LABEL[stats.cv_attribution]}）`}
            ・信頼度 {Math.round(distribution.confidence_level * 100)}%
          </Text>
          {stats.goal_metric !== 'ctr' && stats.cv_attribution !== attribution && (
            <Text size={12} color={TextColor.CAUTION}>
              いま表示しているのは{ATTRIBUTION_LABEL[attribution]}CVです。判定は
              {ATTRIBUTION_LABEL[stats.cv_attribution]}CVで行われています。
            </Text>
          )}
        </ItemAlign>
      </div>

      {/* KPIカード */}
      <div>
        <SummaryHeading>
          パターン別実績
          <ItemAlign align='center' gapX={16}>
            <ConversionAttributionToggle value={attribution} onSelect={setAttribution} />
            <Text size={12}>
              推奨サンプル数：各{commaSeparatedNumber(distribution.min_sample_per_variant)}件
            </Text>
          </ItemAlign>
        </SummaryHeading>
        <ItemAlign gapX={16} full>
          {stats.variants.map((variant, i) => (
            <VariantKpiCard
              key={variant.group}
              variant={variant}
              // 実績と配信のパターンは添字ではなくラベルで対応づける（実績の並びは配信の並びと一致するとは限らない）
              distributionVariant={distribution.variants.find(
                (v) => v.variant_label === variant.group
              )}
              index={i}
              target={distribution.min_sample_per_variant}
              isSuperior={
                distribution.ab_campaign?.winner_variant == null && variant.group == winner
              }
              isWinner={variant.group == distribution.ab_campaign?.winner_variant}
              attribution={attribution}
              disabled={
                distribution.ab_campaign?.winner_variant !== null &&
                variant.group !== distribution.ab_campaign?.winner_variant
              }
              onComplete={() => {
                showModal({
                  message: `パターン ${variant.group} をこのパターンで確定しますか？\n配信停止後、勝者のパターンが確定され、他のパターンは配信停止となります。`,
                  noText: 'キャンセル',
                  yesText: 'このパターンで確定する',
                  onClickYes: async () => {
                    await complete({
                      site_id: siteId,
                      distribution_id: distribution.id,
                      winner_variant: variant.group
                    })
                  }
                })
              }}
            />
          ))}
        </ItemAlign>
      </div>

      {/* 指標サマリーテーブル */}
      {stats.variants.length > 0 ? (
        <Area noBorder>
          <Heading>指標サマリー</Heading>
          <Table>
            <thead>
              <TableRow head>
                {['パターン', '表示数', 'クリック数', 'クリック率', 'CV数', 'CVR', '売上高'].map(
                  (label) => (
                    <TableHeadItem key={label} type='thin'>
                      <Text color={TextColor.SECONDARY} size={12}>
                        {label}
                      </Text>
                    </TableHeadItem>
                  )
                )}
              </TableRow>
            </thead>
            <tbody>
              {rankedVariants.map((variant, index) => {
                const isWinner = winner === variant.group
                return (
                  <TableRow key={variant.group}>
                    <TableRowItem>
                      <ItemAlign gapX={4} align='center'>
                        <Badge badgeType={BadgeType.SQUARE_CAUTION}>
                          <ItemAlign gapX={2} align='center'>
                            <Icon type={IconType.CROWN} color={IconColor.CAUTION} />
                            <Text color={TextColor.CAUTION} lineHeight={150}>
                              {index + 1}
                            </Text>
                          </ItemAlign>
                        </Badge>
                        <span
                          className={'text-size-14 font-bold'}
                          style={{ color: variantColor(index) }}
                        >
                          パターン {variant.group}
                        </span>
                        {isWinner && (
                          <Badge badgeType={BadgeType.SUCCESS} size={10}>
                            優位
                          </Badge>
                        )}
                      </ItemAlign>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{commaSeparatedNumber(variant.impressions)}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{commaSeparatedNumber(variant.clicks)}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>
                        {formatPercent(
                          variant.impressions ? variant.clicks / variant.impressions : 0
                        )}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>
                        {commaSeparatedNumber(pickConversion(variant, attribution).conversions)}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>
                        {formatPercent(
                          variant.impressions
                            ? pickConversion(variant, attribution).conversions / variant.impressions
                            : 0
                        )}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>
                        {currencyText(Math.round(pickConversion(variant, attribution).revenue))}
                      </Text>
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </tbody>
          </Table>
        </Area>
      ) : (
        <Area>
          <GuidanceAreaContent>
            <Text color={TextColor.SECONDARY}>まだ集計できるデータがありません。</Text>
          </GuidanceAreaContent>
        </Area>
      )}

      <TrendChart metric={metric} series={dailySeries?.series ?? []} />
    </Content>
  )
}

/** 配信を停止する（A/Bテスト確定）ボタン。testing 中のみ表示。 */
const StopButton = ({
  siteId,
  distribution,
  onMutated
}: {
  siteId: number
  distribution: RecommendationDistribution
  onMutated: () => void
}) => {
  const { showModal, hide } = useContext(ConfirmDialogContext)
  const { trigger: complete, isMutating } = useCompleteDistribution({
    onSuccess: () => {
      onMutated()
      hide()
    }
  })

  return (
    <Button
      color={ButtonColor.CRITICAL}
      loading={isMutating}
      onClick={() =>
        showModal({
          message:
            '本A/Bテストおよび配信を再開することはできません。パターンを確定してテストを終了したい場合は、「このパターンで確定する」ボタンを押下してテストを終了してください。',
          noText: 'キャンセル',
          yesText: '配信を停止する',
          buttonColor: ButtonColor.CRITICAL,
          onClickYes: async () => {
            await complete({ site_id: siteId, distribution_id: distribution.id })
          }
        })
      }
      disabled={distribution.status !== RecommendationDistributionStatus.TESTING}
    >
      配信を停止する
    </Button>
  )
}

const RecommendationDistributionAbCampaignContent = ({
  siteId,
  distribution,
  onMutated
}: {
  siteId: number
  distribution: RecommendationDistribution
  onMutated: () => void
}) => {
  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title={`A/Bテスト結果 - ${distribution.name}`}
        badgeNode={<AppTitleBadge type='recommend' />}
        description={
          `${distribution.ab_variant_count}パターン (${distribution.variants
            .map((v) => v.ab_traffic)
            .join(':')}) / ` +
          (distribution.ab_campaign?.started_at
            ? `開始 ${dateStr(distribution.ab_campaign.started_at)}〜${distribution.ab_campaign.completed_at ? dateTimeStr(distribution.ab_campaign.completed_at) : '継続中'}`
            : '未開始')
        }
        behindNode={
          <Badge badgeType={distributionStatusBadgeType(distribution.status)}>
            {distributionStatusName(distribution.status)}
          </Badge>
        }
        rightNode={<StopButton siteId={siteId} distribution={distribution} onMutated={onMutated} />}
      />
      {distribution.ab_campaign ? (
        <AbResultView siteId={siteId} distribution={distribution} onMutated={onMutated} />
      ) : (
        <Content>
          <Area>
            <GuidanceAreaContent>
              <Text color={TextColor.SECONDARY}>この配信にはA/Bテストがありません。</Text>
            </GuidanceAreaContent>
          </Area>
        </Content>
      )}
    </Main>
  )
}

const RecommendationDistributionAbCampaignPage = () => {
  const siteId = useSiteId()
  const { distributionId } = useParams()

  const { data, error, mutate } = useDistributionGetApi({
    siteId,
    distributionId: Number(distributionId)
  })

  return (
    <DetailPageLoader error={error} data={data}>
      {data && (
        <RecommendationDistributionAbCampaignContent
          siteId={siteId}
          distribution={data}
          onMutated={() => mutate()}
        />
      )}
    </DetailPageLoader>
  )
}

export default RecommendationDistributionAbCampaignPage
